import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Stack,
  Switch,
  Link,
  Button,
} from "@mui/material";
import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunchOutlined";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import PhoneIcon from "@mui/icons-material/PhoneOutlined";
import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import PublicIcon from "@mui/icons-material/PublicOutlined";
import GroupsIcon from "@mui/icons-material/GroupsOutlined";
import StoreIcon from "@mui/icons-material/StorefrontOutlined";
import CalendarIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTimeOutlined";
import InitialsAvatar from "src/components/InitialsAvatar";
import StatusPill, { Tone } from "src/components/StatusPill";
import ConvertCompanyModal from "./ConvertCompanyModal";

interface Props {
  open: boolean;
  onClose: () => void;
  data: any;
  onToggleContacted?: (checked: boolean) => void;
}

const statusTone = (status: string): Tone => {
  switch (status) {
    case "NEW": return "info";
    case "CONTACTED": return "primary";
    case "QUALIFIED":
    case "RESCHEDULED": return "warning";
    case "WON":
    case "CONVERTED": return "success";
    case "LOST":
    case "SPAM":
    case "CLOSED": return "error";
    default: return "neutral";
  }
};

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography fontSize="11px" fontWeight={800} color="text.secondary" textTransform="uppercase" letterSpacing="0.06em" mb={1}>
    {children}
  </Typography>
);

const InfoRow = ({ label, value, icon }: { label: string; value?: any; icon?: React.ReactNode }) => (
  <Box display="flex" alignItems="center" justifyContent="space-between" py={0.75}>
    <Stack direction="row" spacing={1.25} alignItems="center">
      {icon && <Box sx={{ color: "text.disabled", display: "flex", "& svg": { fontSize: 18 } }}>{icon}</Box>}
      <Typography fontSize="13px" color="text.secondary">{label}</Typography>
    </Stack>
    <Box sx={{ textAlign: "right", maxWidth: "60%" }}>
      <Typography fontSize="13px" fontWeight={600} sx={{ wordBreak: "break-word" }} component="div">
        {value ?? "—"}
      </Typography>
    </Box>
  </Box>
);

const SoftCard = ({ children, accent }: { children: React.ReactNode; accent?: string }) => (
  <Box
    sx={{
      p: 1.75,
      borderRadius: "10px",
      bgcolor: accent || "#FBFCFE",
      border: "1px solid #EEF2F6",
    }}
  >
    {children}
  </Box>
);

const ContactDetailsDrawer = ({ open, onClose, data, onToggleContacted }: Props) => {
  const [convertOpen, setConvertOpen] = useState(false);
  if (!data) return null;

  const isConverted = !!data.converted_company_uuid || data.pipeline_status === "CONVERTED";

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { borderTopLeftRadius: 0, boxShadow: "-12px 0 32px rgba(15,23,42,0.08)" } }}>
      <Box width={{ xs: "100vw", sm: 420 }} height="100%" display="flex" flexDirection="column" sx={{ bgcolor: "#fff" }}>
        {/* Close */}
        <Box px={2.5} pt={2} display="flex" justifyContent="flex-end">
          <IconButton onClick={onClose} size="small" sx={{ border: "1px solid #E6EBF2", borderRadius: "9px" }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Hero */}
        <Box px={2.5} pb={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <InitialsAvatar name={data.name} size={44} />
            <Box minWidth={0}>
              <Typography fontSize="17px" fontWeight={800} letterSpacing="-0.2px" noWrap>{data.name}</Typography>
              <Typography fontSize="13px" color="text.secondary" noWrap>{data.company || "—"}</Typography>
            </Box>
          </Box>
          <Box display="flex" flexDirection="row" gap={0.75} mt={1.5} alignItems="center" flexWrap="wrap">
            <StatusPill label={data.status} tone={statusTone(data.status)} />
            <StatusPill label={data.contacted ? "Contacted" : "Not contacted"} tone={data.contacted ? "success" : "neutral"} />
            {isConverted && <StatusPill label="Converted" tone="success" />}
          </Box>
        </Box>

        {/* Body */}
        <Box px={2.5} pb={2.5} flex={1} overflow="auto" display="flex" flexDirection="column" gap={2.25}>
          {/* Contacted toggle */}
          {onToggleContacted && (
            <SoftCard>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography fontSize="14px" fontWeight={600}>Mark as contacted</Typography>
                  <Typography fontSize="12px" color="text.secondary">Toggle once you've reached out.</Typography>
                </Box>
                <Switch checked={data.contacted} onChange={(e) => onToggleContacted(e.target.checked)} />
              </Box>
            </SoftCard>
          )}

          {/* Details */}
          <Box>
            <SectionLabel>Contact details</SectionLabel>
            <Box sx={{ "& > div + div": { borderTop: "1px solid #F1F4F8" } }}>
              <InfoRow icon={<EmailIcon />} label="Email" value={<Link href={`mailto:${data.email}`} underline="hover">{data.email}</Link>} />
              <InfoRow icon={<PhoneIcon />} label="Phone" value={data.phone} />
              <InfoRow icon={<PublicIcon />} label="Country" value={data.country} />
              <InfoRow icon={<BusinessIcon />} label="Business type" value={data.businessType} />
              <InfoRow icon={<GroupsIcon />} label="Employees" value={data.employees} />
              <InfoRow icon={<StoreIcon />} label="Locations" value={data.locations} />
              <InfoRow icon={<CalendarIcon />} label="Preferred date" value={data.preferred_contact_date ? new Date(data.preferred_contact_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null} />
              <InfoRow icon={<AccessTimeIcon />} label="Preferred time" value={data.preferred_contact_time} />
            </Box>
          </Box>

          {/* Message */}
          <Box>
            <SectionLabel>Message</SectionLabel>
            <SoftCard>
              <Typography fontSize="14px" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                {data.message || "—"}
              </Typography>
            </SoftCard>
          </Box>

          {/* Remarks */}
          <Box>
            <SectionLabel>Internal remarks</SectionLabel>
            <SoftCard accent="#FFFBEB">
              <Typography fontSize="14px" color="text.secondary">
                {data.remarks || "No remarks added yet."}
              </Typography>
            </SoftCard>
          </Box>

          {/* Meta */}
          <Box>
            <SectionLabel>Meta</SectionLabel>
            <Stack spacing={0.5}>
              <Typography fontSize="12px" color="text.secondary">Source: {data.source || "Website"}</Typography>
              <Typography fontSize="12px" color="text.secondary">IP address: {data.ipAddress || "N/A"}</Typography>
              <Typography fontSize="12px" color="text.secondary">Created: {new Date(data.createdAt).toLocaleString()}</Typography>
            </Stack>
          </Box>
        </Box>

        {/* Footer */}
        <Box px={2.5} py={1.75} borderTop="1px solid" borderColor="#EEF2F6">
          <Button
            fullWidth
            variant="contained"
            startIcon={<RocketLaunchIcon />}
            disabled={isConverted}
            onClick={() => setConvertOpen(true)}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", height: 42, boxShadow: "none" }}
          >
            {isConverted ? "Already Converted" : "Convert to Company (Free Trial)"}
          </Button>
        </Box>
      </Box>

      <ConvertCompanyModal open={convertOpen} onClose={() => setConvertOpen(false)} contact={data} onConverted={onClose} />
    </Drawer>
  );
};

export default ContactDetailsDrawer;
